// =============================================================================
// SHANIZ GAMING ADMIN - WEBSOCKET LIVE FEED HOOK
// =============================================================================

import { useEffect, useRef, useState, useCallback } from 'react';

export type AdminSocketEvent =
  | { type: 'live.metrics'; payload: { dau: number; activeSessions: number; revenueToday: number } }
  | { type: 'game.update'; payload: { id: string; status: string; gameType?: string } }
  | { type: 'game.started'; payload: { id: string; gameType: string; player1?: string; player2?: string } }
  | { type: 'game.ended'; payload: { id: string; winnerId?: string } }
  | { type: 'fraud.alert'; payload: { id: string; severity: string; userId: string; title: string; createdAt: string } }
  | { type: 'ticket.message'; payload: { ticketId: string; messageId: string; by: string } }
  | { type: 'notification'; payload: { id: string; title: string; body: string; level: string; at: string } };

type Listener = (ev: AdminSocketEvent) => void;

function resolveSocketUrl(): string {
  const base = process.env.REACT_APP_WS_URL || process.env.REACT_APP_API_URL || 'http://16.60.164.64:4065';
  // Convert http(s) → ws(s) and append the admin live path.
  const url = base
    .replace(/^https:\/\//, 'wss://')
    .replace(/^http:\/\//, 'ws://')
    .replace(/\/api\/v1\/?$/, '');
  return `${url}/admin/live`;
}

class AdminSocket {
  private ws: WebSocket | null = null;
  private listeners = new Set<Listener>();
  private reconnectTimer: number | null = null;
  private reconnectAttempts = 0;
  private closedByUser = false;
  public status: 'connecting' | 'open' | 'closed' = 'closed';

  connect() {
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      return;
    }
    this.closedByUser = false;
    this.status = 'connecting';
    try {
      const url = resolveSocketUrl();
      const token = localStorage.getItem('admin_token');
      const fullUrl = token ? `${url}?token=${encodeURIComponent(token)}` : url;
      this.ws = new WebSocket(fullUrl);

      this.ws.onopen = () => {
        this.status = 'open';
        this.reconnectAttempts = 0;
        this.emit({ type: 'notification', payload: { id: 'ws-open', title: 'Live feed', body: 'Connected', level: 'info', at: new Date().toISOString() } });
      };
      this.ws.onmessage = (evt) => {
        try {
          const parsed = JSON.parse(evt.data);
          if (parsed && typeof parsed.type === 'string') this.emit(parsed as AdminSocketEvent);
        } catch {
          /* ignore malformed frames */
        }
      };
      this.ws.onclose = () => {
        this.status = 'closed';
        if (!this.closedByUser) this.scheduleReconnect();
      };
      this.ws.onerror = () => {
        try {
          this.ws?.close();
        } catch {
          /* noop */
        }
      };
    } catch {
      this.scheduleReconnect();
    }
  }

  private scheduleReconnect() {
    if (this.reconnectTimer) window.clearTimeout(this.reconnectTimer);
    const delay = Math.min(30_000, 1000 * Math.pow(2, this.reconnectAttempts));
    this.reconnectAttempts += 1;
    this.reconnectTimer = window.setTimeout(() => this.connect(), delay);
  }

  disconnect() {
    this.closedByUser = true;
    if (this.reconnectTimer) window.clearTimeout(this.reconnectTimer);
    this.ws?.close();
    this.ws = null;
  }

  send(msg: unknown) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
    }
  }

  subscribe(l: Listener) {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }

  private emit(ev: AdminSocketEvent) {
    this.listeners.forEach((l) => {
      try {
        l(ev);
      } catch {
        /* swallow listener errors so one handler cannot break others */
      }
    });
  }
}

// Singleton instance — one connection per browser tab.
export const adminSocket = new AdminSocket();

export function useAdminSocket(listener?: (ev: AdminSocketEvent) => void) {
  const [status, setStatus] = useState<'connecting' | 'open' | 'closed'>(adminSocket.status);
  const listenerRef = useRef(listener);
  listenerRef.current = listener;

  useEffect(() => {
    adminSocket.connect();
    const unsub = adminSocket.subscribe((ev) => {
      if (ev.type === 'notification' && ev.payload.id === 'ws-open') setStatus('open');
      listenerRef.current?.(ev);
    });
    const poll = window.setInterval(() => setStatus(adminSocket.status), 2000);
    return () => {
      unsub();
      window.clearInterval(poll);
    };
  }, []);

  const send = useCallback((msg: unknown) => adminSocket.send(msg), []);
  return { status, send };
}
